// =========================================================================
// Home — the section landing. Route `/home`, Full mode only.
// Demo `renderSectionPage('home')`, source lines 3314 and 3364.
//
// Two bands, in the demo's order:
//
//   1. **Hero** — the section mark over the contour background, the lead
//      paragraph, and four figures: machines, open jobs, open alerts, fleet
//      availability.
//   2. **Eight tiles** — the Home section's own pages, each with its count.
//
// Executive mode lands on Asset360 at `/`; `routeForView('section', 'home')`
// only resolves here when the demo mode is Full. The page is not in the rail.
//
// UI layer. Fork-owned (zone C), L5 lane.
// =========================================================================

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpAnchor, SurfacePageBody, SurfacePageLayout } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { avgAvail, jobs as buildJobs } from '@/versions/v1/shared/oem-engine';
import { useOemData, type WorkOrder } from '@/versions/v1/shared/useOemData';
import type { ShiftDailyRow } from '@/versions/v1/schema/telemetry';
import { SectionHero } from './SectionHero';
import { SectionTile } from './SectionTile';
import {
  HOME_COLOUR,
  HOME_ICON,
  HOME_TAG,
  homeLead,
  homeStats,
  homeTiles,
} from './HomeSectionScene.transform';

/**
 * The two live figures — the open-job count and fleet availability.
 *
 * Three domains rather than the workflow context's eight: nothing else on the
 * page is live, and a landing should not pay for the Maximo register.
 */
function useHomeFigures() {
  const workOrders = useOemData('work-orders');
  const service = useOemData('service');
  const telemetry = useOemData('telemetry');

  const loading = workOrders.loading || service.loading || telemetry.loading;
  const error = workOrders.error ?? service.error ?? telemetry.error ?? null;

  const openJobs = useMemo<number | null>(() => {
    if (!workOrders.data || !service.data) return null;
    return buildJobs({
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
    }).length;
  }, [workOrders.data, service.data]);

  const availability = useMemo<number | null>(() => {
    if (!telemetry.data) return null;
    return avgAvail({ shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily) });
  }, [telemetry.data]);

  return { openJobs, availability, loading, error };
}

export function HomeSectionScene() {
  const navigate = useNavigate();
  const { openJobs, availability, loading, error } = useHomeFigures();

  const stats = useMemo(() => homeStats(openJobs, availability), [openJobs, availability]);
  const tiles = useMemo(() => homeTiles(openJobs), [openJobs]);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="HomeSectionScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8 pt-6">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The aftermarket data could not be loaded ({error.message}). The open-job count
              and fleet availability are not shown rather than shown wrong.
            </div>
          ) : null}

          <Commentable name="Hero">
            <SectionHero
              colour={HOME_COLOUR}
              icon={HOME_ICON}
              title="Home"
              tag={HOME_TAG}
              lead={homeLead()}
              stats={stats}
              actions={<HelpAnchor doc="workflows/home" />}
            />
          </Commentable>

          <AskStrip />

          <Commentable name="Tiles">
            <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3 @6xl:grid-cols-4">
              {tiles.map((tile) => (
                <SectionTile
                  key={tile.id}
                  colour={HOME_COLOUR}
                  icon={tile.icon}
                  label={tile.label}
                  description={tile.description}
                  count={tile.count}
                  onSelect={() => navigate(tile.to)}
                />
              ))}
            </div>
          </Commentable>

          {loading && openJobs === null ? (
            <p className="sr-only" role="status">
              Loading the aftermarket data.
            </p>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default HomeSectionScene;
